"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { useEffect } from "react";

import { PREDEPOSIT_USDC_REWARDS_SEEN_KEY } from "@/constants";
import { usePredepositUsdcRewards } from "@/contexts/queries/points/predeposit-usdc-rewards";
import { useLocalStorage } from "@/hooks/storage";
import { useAuthState } from "@/hooks/use-auth-state";
import { linkStyles } from "@/recipes/link";

import { HandleSuspenseQuery } from "./handle-query";
import { toast } from "./toast";

export function PredepositUsdcRewardsNotification() {
  return (
    // No need to show the loading or error state for this background task
    <HandleSuspenseQuery loading={null} error={() => null}>
      <PredepositUsdcRewardsNotificationContent />
    </HandleSuspenseQuery>
  );
}

function PredepositUsdcRewardsNotificationContent() {
  const t = useTranslations("preDeposits.usdcRewardsNotification");
  const { accountAddress } = useAuthState();
  const { data: rewardsData, isError } = usePredepositUsdcRewards(accountAddress);
  const storageKey = accountAddress
    ? `${PREDEPOSIT_USDC_REWARDS_SEEN_KEY}-${accountAddress}`
    : PREDEPOSIT_USDC_REWARDS_SEEN_KEY;
  const [hasSeen, setHasSeen, isLoading] = useLocalStorage<boolean>(storageKey, false);

  useEffect(() => {
    if (isLoading || isError || !rewardsData || rewardsData.usdc_reward <= 0 || hasSeen) return;

    const toastId = "predeposit-usdc-rewards";
    queueMicrotask(() => {
      toast({
        id: toastId,
        accountScoped: true,
        variant: "success",
        title: t("title"),
        description: () => (
          <span>
            {t.rich("description", {
              link: (chunks) => (
                <Link href="/points" className={linkStyles} onClick={() => toast.dismiss(toastId)}>
                  {chunks}
                </Link>
              ),
            })}
          </span>
        ),
        duration: Infinity,
      });
      setHasSeen(true);
    });
  }, [rewardsData, isLoading, isError, hasSeen, setHasSeen, t]);

  return null;
}
